import { RefObject, useLayoutEffect, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'

const pxToNum = (px: string) => Number(px.replace('px', ''))

/**
 * Hook to detect if a container's content should wrap to a new line.
 */
export const useRowWrapDetector = <T extends HTMLElement>(
  ref: RefObject<T | null>,
) => {
  const { height } = useResizeDetector({ targetRef: ref })
  // Must be 1, not zero, or something stops the calculations.
  const [firstRowHeight, setFirstRowHeight] = useState<number>(1)
  const [containerPadding, setContainerPadding] = useState<number>(0)
  const [isWrapping, setIsWrapping] = useState<boolean>(false)

  const calculate = () => {
    if (!ref.current) return

    const children = Array.from(ref.current.children)
    const firstChild = children[0]
    const firstRowChildren = children.filter(
      child =>
        child.getClientRects()[0].top === firstChild.getClientRects()[0].top,
    )
    const isRowWrapping = firstRowChildren.length < children.length
    const sortedHeights = firstRowChildren
      .map(child => child.getClientRects()[0].height)
      .sort((a, b) => b - a)

    const maxChildHeight = sortedHeights[0]

    const computedParent = window.getComputedStyle(ref.current)
    const totalPadding =
      pxToNum(computedParent.paddingTop) + pxToNum(computedParent.paddingBottom)

    setIsWrapping(isRowWrapping)
    setContainerPadding(totalPadding)
    if (firstChild) setFirstRowHeight(maxChildHeight)
  }

  useLayoutEffect(() => {
    calculate()
  }, [height])

  return {
    height: height || 0,
    isWrapping,
    firstRowHeight,
    containerPadding,
  }
}

/**
 * What should the container height should be?
 * if container height is greater than it should be with a single row.
 * Set isWrapping
 */
